/**
 * login.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { Modal } from "angular2-modal/plugins/bootstrap";
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs/Rx";

import { LoginRequest } from "../server/messages/auth/";
import {
  ModifySecurityQuestionsClientResponse,
  ModifySecurityQuestionsServerRequest,
  NewPasswordClientResponse,
  NewPasswordServerRequest
} from "../server/messages/auth/credentials/";
import {
  AcknowledgmentResponse,
  Message,
  MessageTag
} from "../server/messages/general/";
import { MessagingService } from "../server/messaging.service";
import { ModalHelpers } from "../support/utilities";

import { AuthService, AuthState } from "./auth.service";
import { ChangePasswordModalComponent }
  from "./change-password-modal.component";
import { ChangeSecurePromptsModalComponent }
  from "./change-secure-prompts-modal.component";
import { Hasher } from "./hasher.service";
import { PasswordCredentials } from "./identity/credentials";
import { Salter } from "./salter.service";

@Injectable()
export class LoginService
{
    constructor (
        public messaging : MessagingService,
        public auth : AuthService,
        public modal : Modal
    )
    {
        messaging.ready.subscribe(
          (msgStatus : boolean) => this.ready = msgStatus);
    }

    public ready : boolean = false;

  /**
   * Submit a set of {@link PasswordCredentials} to the server in an attempt to
   * authenticate the user, and update the connection's authentication state
   * appropriately based upon the response.
   *
   * @param  {PasswordCredentials} credentials User-provided credentials
   * @return {Observable<boolean>} An observable of the attempt's result
   */
  public attempt (credentials : PasswordCredentials) : Observable<boolean>
  {
    if (!this.ready)
    {
      alert("The credential service was not ready for you to login.");
      return Observable.of(false);
    }
    this.ready = false;
    return this.messaging.send(new LoginRequest(
      credentials.username,
      credentials.hashedPassword
    )).flatMap((m : Message) => {
      if (Message.isA(m, MessageTag.SREQ_NEW_PASSWORD))
      {
        return this.handleServerPasswordChangeRequest(
            m as NewPasswordServerRequest);
      }
      return Observable.of(m);
    }).flatMap((m : Message) => {
      if (m && Message.isA(m, MessageTag.SREQ_MODIFY_SECURITY_QUESTIONS))
      {
        return this.handleServerPromptChangeRequest(
            m as ModifySecurityQuestionsServerRequest);
      }
      return Observable.of(m);
    }).flatMap((m : Message) => {
      if (m && Message.isA(m, MessageTag.RSP_ACKNOWLEDGEMENT))
      {
        return [
            this.auth.inferStatusFromAck(m as AcknowledgmentResponse, true)
        ];
      }
      console.error("unknown auth response", m);
      return [false];
    }).do((authResult : boolean) =>
        ((authResult === false)
          ? this.messaging.restart(true, true) // (allow infinite attempts)
          : null),
        (err) => console.error("Login error", err),
        () => this.ready = true);
  }

  public handleServerPasswordChangeRequest (m : NewPasswordServerRequest)
    : Observable<Message>
  {
    this.auth.state = AuthState.EnforcedCredentialChange;
    return ModalHelpers.observeCustom<Message>(
      this.modal,
      ChangePasswordModalComponent,
      {size: "sm", enforced: true}
    );
  }

  public handleServerPromptChangeRequest (
    m : ModifySecurityQuestionsServerRequest
  ) : Observable<Message>
  {
    this.auth.state = AuthState.EnforcedCredentialChange;
    return ModalHelpers.observeCustom<Message>(
      this.modal,
      ChangeSecurePromptsModalComponent,
      {minimumPrompts: m.required, enforced: true}
    );
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/auth/login.service.ts